import {
  API_ROUTES,
  fetchUserFileDocument,
  postEncryptedJson,
} from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { toApiDateParam } from "../../utils/dateRangeApiFormat";

/**
 * Cash-in Request Controller
 * Calls the encrypted get-all-pending-cashin endpoint and normalizes the result.
 *
 * API: {{base_url}}/v1/user/get-all-pending-cashin?page=1
 * Logical BODY before encryption:
 * {
 *   "PER_PAGE": 10,
 *   "DATE_FROM": "2026-01-01T00:00:00.000",
 *   "DATE_TO": "2026-02-02T00:00:00.000"
 * }
 *
 * Encryption, device headers and Authorization: Bearer {token}
 * are handled centrally by apiClient.postEncryptedJson.
 */
class CashInRequestController {
  static extractFilePathsDeep(value) {
    const out = [];
    const seen = new Set();
    const pushPath = (candidate) => {
      const s = String(candidate || "").trim();
      if (!s || seen.has(s)) return;
      seen.add(s);
      out.push(s);
    };
    const visit = (node, depth = 0) => {
      if (node == null || depth > 12) return;
      if (Array.isArray(node)) {
        node.forEach((item) => visit(item, depth + 1));
        return;
      }
      if (typeof node !== "object") return;
      const direct =
        node.FILE_PATH ??
        node.file_path ??
        node.filePath ??
        node.PATH ??
        node.path ??
        null;
      if (direct) pushPath(direct);
      Object.values(node).forEach((child) => visit(child, depth + 1));
    };
    visit(value, 0);
    return out;
  }

  static normalizeCashInItem(item) {
    const requestToken =
      item?.REQUEST_TOKEN ?? item?.requestToken ?? item?.request_token ?? null;

    const id =
      requestToken ?? item?.REF_ID ?? item?.REFID ?? item?.ID ?? item?.id ?? null;

    const date =
      item?.UPDATED_AT ||
      item?.REQUEST_DATE ||
      item?.CREATED_AT ||
      item?.DATE ||
      item?.date ||
      null;

    const amount = item?.AMOUNT ?? item?.amount ?? item?.VALUE ?? null;

    const methodLabel =
      item?.METHOD_LABEL ||
      item?.METHOD_NAME ||
      item?.METHOD ||
      item?.method ||
      null;

    const walletLabel =
      item?.WALLET_LABEL ||
      item?.WALLET_NAME ||
      item?.walletLabel ||
      item?.walletName ||
      null;

    const statusLabel =
      item?.STATUS_LABEL ||
      item?.STATUS_NAME ||
      item?.STATUS ||
      item?.status ||
      null;

    const currencySymbol =
      item?.CURRENCY_SYMBOL ||
      (item?.CURRENCY &&
        (item.CURRENCY.SYMBOL || item.CURRENCY.CURRENCY_SYMBOL)) ||
      item?.currencySymbol ||
      null;

    return {
      id,
      requestToken,
      date,
      amount,
      methodLabel,
      walletLabel,
      statusLabel,
      currencySymbol,
      raw: item,
    };
  }

  /**
   * Fetch pending cash-in requests from backend with encryption handled by apiClient.
   *
   * @param {Object} params
   * @param {number} [params.page=0] - Zero-based page index (also sent as ?page=).
   * @param {number} [params.perPage=10] - Items per page.
   * @param {string|null} [params.dateFrom] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {string|null} [params.dateTo] - ISO-like string "YYYY-MM-DDTHH:mm:ss.SSS" or null.
   * @param {Object} [options] - Optional postEncryptedJson options (e.g., baseURL override).
   *
   * @returns {Promise<{
   *   items: Array,
   *   pagination: { page: number, perPage: number, total?: number, lastPage?: number },
   *   raw: any
   * }>}
   */
  static async getAllPendingCashIn(
    {
      page = 0,
      perPage = 10,
      dateFrom = null,
      dateTo = null,
    } = {},
    options = {}
  ) {
    const body = {
      PER_PAGE: perPage,
      DATE_FROM: toApiDateParam(dateFrom, false),
      DATE_TO: toApiDateParam(dateTo, true),
    };

    // Backend pagination is 1-based (matches Flutter implementation)
    const urlWithPage = `${API_ROUTES.GET_ALL_PENDING_CASHIN}?page=${page + 1}`;

    const res = await postEncryptedJson(urlWithPage, body, options);
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg =
        d.MESSAGE ||
        d.message ||
        d.ERROR ||
        `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    // Expect Laravel-style paginator in DATA, similar to get-all-transactions.
    const dataBlock = d.DATA || {};
    let list = [];

    if (Array.isArray(dataBlock.data)) {
      list = dataBlock.data;
    } else if (Array.isArray(dataBlock.DATA)) {
      list = dataBlock.DATA;
    } else if (Array.isArray(dataBlock.items)) {
      list = dataBlock.items;
    } else if (Array.isArray(dataBlock.requests)) {
      list = dataBlock.requests;
    } else if (Array.isArray(dataBlock.list)) {
      list = dataBlock.list;
    }

    // Normalize for UI but keep raw item as reference.
    const items = list.map((item) => CashInRequestController.normalizeCashInItem(item));

    // Convert Laravel 1-based pagination to 0-based indexes for the UI
    const currentPage = Number(dataBlock.current_page ?? 1);
    const lastPage = Number(dataBlock.last_page ?? 1);
    const perPageVal = Number(dataBlock.per_page ?? perPage) || perPage;
    const totalVal = Number(dataBlock.total ?? dataBlock.TOTAL ?? 0);

    const pagination = {
      page: currentPage - 1,
      perPage: perPageVal,
      total: totalVal,
      lastPage: lastPage - 1,
    };

    return {
      items,
      pagination,
      raw: d,
    };
  }

  static async getCashInByRequestToken(requestToken, options = {}) {
    const token = String(requestToken || "").trim();
    if (!token) {
      return { items: [], pagination: { page: 0, perPage: 10, total: 0, lastPage: 0 }, raw: null };
    }

    const res = await postEncryptedJson(API_ROUTES.GET_CASHIN, { REQUEST_TOKEN: token }, options);
    const d = res.decryptedData || res.data || {};
    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg =
        d.MESSAGE ||
        d.message ||
        d.ERROR ||
        `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const row = d.DATA;
    const items = row && typeof row === "object"
      ? [CashInRequestController.normalizeCashInItem(row)]
      : [];

    return {
      items,
      pagination: { page: 0, perPage: 10, total: items.length, lastPage: 0 },
      raw: d,
    };
  }

  /**
   * Cancel a pending cash-in request by REQUEST_TOKEN.
   *
   * API: {{base_url}}/v1/user/cancel-cash-in-operation
   * BODY (logical structure before encryption):
   * {
   *   "REQUEST_TOKEN": "435122-265121-875624"
   * }
   */
  static async cancelCashIn(requestToken, options = {}) {
    if (!requestToken) {
      throw new Error("Request token is required.");
    }

    const body = {
      REQUEST_TOKEN: requestToken,
    };

    const res = await postEncryptedJson(
      API_ROUTES.CANCEL_CASHIN_MONEY_OPERATION,
      body,
      options
    );
    const d = res.decryptedData || res.data || {};

    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg =
        d.MESSAGE ||
        d.message ||
        d.ERROR ||
        `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    return d;
  }

  static async getPendingCashinGatewayInformation(transactionId, options = {}) {
    const txId = String(transactionId || "").trim();
    if (!txId) {
      throw new Error("Transaction id is required.");
    }

    const res = await postEncryptedJson(
      API_ROUTES.GET_PENDING_CASHIN_GATEWAY_INFORMATION,
      { TRANSACTION_ID: txId },
      options
    );
    const d = res.decryptedData || res.data || {};
    if (!d || Number(d.CODE) !== AppConstants.responseOk) {
      const msg =
        d.MESSAGE ||
        d.message ||
        d.ERROR ||
        `API Error (CODE: ${d.CODE ?? "Unknown"})`;
      throw new Error(msg);
    }

    const row = d.DATA || {};
    const documents = CashInRequestController.extractFilePathsDeep(row);
    return { documents, raw: row };
  }

  static async downloadPendingCashinDocument(filePath, options = {}) {
    const cleanPath = String(filePath || "").trim();
    if (!cleanPath) {
      throw new Error("Document path is required.");
    }
    const response = await fetchUserFileDocument("cashinRequest", cleanPath, options);
    return response?.data;
  }
}

export default CashInRequestController;

